import { AdjustmentNote, AdjustmentNoteType, CashFund, Check, CreditCard, CreditPayment, Employee, Expense, ExpenseType, PaymentOrder, PaymentType, Ticket, useCheckHooks, useCreditCardHooks, useEmployeeHooks, useTicketHooks } from "../../../modules/administration"
import { ticketHelpers } from "../../../modules/administration/hooks/ticketHooks"
import { Delivery, DeliveryOrder, deliveryOrderHelpers, deliveryHelpers } from "../../../modules/deliveries"
import { Driver, Roadmap, Vehicle, useVehicleHooks } from "../../../modules/logistics"
import { Branch } from "../../../modules/manager"
import { Category, Product } from "../../../modules/products"
import { PurchaseOrder, Supplier } from "../../../modules/purchases"
import { Budget, Customer, CustomerType, HoardOrder, SaleOrder, useCustomerHooks } from "../../../modules/sales"
import { ArchivableDataType, CancellableDataType, CodedDataType, DataType, NamedDataType, TableName } from "../../../tables"
import { Address, addressHelpers } from "../../address"

export const useDescribeHooks = () => {
    const { describeAddress } = addressHelpers
    const { describeCode } = describeHelpers
    const { describeDeliveryOrder } = deliveryOrderHelpers
    const { describeDelivery } = deliveryHelpers
    const { describeTicket } = ticketHelpers
    const { describeCheck } = useCheckHooks()
    const { describeCreditCard } = useCreditCardHooks()
    const { describeCustomer } = useCustomerHooks()
    const { describeEmployee } = useEmployeeHooks()
    const { isSalary } = useTicketHooks()
    const { describeVehicle } = useVehicleHooks()

    const describe = (table: TableName, dataType: DataType) => {
        const expenseNoun = (expense: Expense) => isSalary(table, expense.id) ? 'Sueldo' : 'Gasto'

        const resultFn = new Map<TableName, () => string>()
            .set(TableName.ADJUSTMENT_NOTES, () => `${(dataType as AdjustmentNote).type === AdjustmentNoteType.DEBIT ? 'Nota de Débito' : 'Nota de Crédito'} ${describeCode(dataType as AdjustmentNote)}`)
            .set(TableName.ADDRESSES, () => describeAddress(dataType as Address))
            .set(TableName.BRANCHES, () => `Sucursal: ${(dataType as Branch).name}`)
            .set(TableName.BUDGETS, () => `Presupuesto ${describeCode(dataType as Budget)}`)
            .set(TableName.CASH_FUNDS, () => `Caja: ${(dataType as CashFund).name}`)
            .set(TableName.CATEGORIES, () => `Categoría: ${(dataType as Category).name}`)
            .set(TableName.CHECKS, () => `Cheque: ${describeCheck(dataType as Check)}`)
            .set(TableName.CREDIT_CARDS, () => `Tarjeta: ${describeCreditCard(dataType as CreditCard)}`)
            .set(TableName.CREDIT_PAYMENTS, () => `Cobro ${describeCode(dataType as CreditPayment)}`)
            .set(TableName.CUSTOMER_TYPES, () => `Lista de Precios: ${(dataType as CustomerType).name}`)
            .set(TableName.CUSTOMERS, () => `Cliente: ${describeCustomer(dataType as Customer)}`)
            .set(TableName.DELIVERIES2, () => describeDelivery(dataType as Delivery))
            .set(TableName.DELIVERY_ORDERS, () => describeDeliveryOrder(dataType as DeliveryOrder))
            .set(TableName.DRIVERS, () => `Conductor: ${(dataType as Driver).name}`)
            .set(TableName.EMPLOYEES, () => `Empleado: ${describeEmployee(dataType as Employee)}`)
            .set(TableName.EXPENSE_TYPES, () => `Tipo de Gasto: ${(dataType as ExpenseType).name}`)
            .set(TableName.EXPENSES, () => `${expenseNoun(dataType as Expense)}: ${(dataType as Expense).name}`)
            .set(TableName.HOARD_ORDERS, () => `Acopio ${describeCode(dataType as HoardOrder)}`)
            .set(TableName.PAYMENT_ORDERS, () => `Orden de Pago ${describeCode(dataType as PaymentOrder)}`)
            .set(TableName.PAYMENT_TYPES, () => `Medio de Pago: ${(dataType as PaymentType).name}`)
            .set(TableName.PURCHASE_ORDERS, () => `Compra ${describeCode(dataType as PurchaseOrder)}`)
            .set(TableName.PRODUCTS, () => `Producto: ${(dataType as Product).name}`)
            .set(TableName.ROADMAPS, () => `Hoja de Ruta ${describeCode(dataType as Roadmap)}`)
            .set(TableName.SALE_ORDERS, () => `Venta ${describeCode(dataType as SaleOrder)}`)
            .set(TableName.SUPPLIERS, () => `Proveedor: ${(dataType as Supplier).name}`)
            .set(TableName.TICKETS, () => describeTicket(dataType as Ticket))
            .set(TableName.VEHICLES, () => `Vehículo: ${(describeVehicle(dataType as Vehicle))}`)

        const result = resultFn.get(table)?.()
        const description = result || (dataType as NamedDataType).name || dataType.id
        const statusLabel = (dataType as ArchivableDataType).archived ? ' (ARCHIVADO)' : ((dataType as CancellableDataType).cancelled ? ' (CANCELADO)' : '')
        return `${description}${statusLabel}`
    }

    return {
        describe
    }
}

export const describeHelpers = {
    describeCode: (dataType: CodedDataType) => `#${dataType.code}`
}
